<template>
  <div
    class="customer-display"
    :style="{ backgroundImage: pictureUrl ? `url(${pictureUrl})` : 'none' }"
  >
    <div v-if="!isUserLoggedIn">User is not logged in, screen is sleeping</div>
    <!-- CustomerBasket renders only once the main window has broadcast the basket payload -->
    <CustomerBasket
      v-if="basketData"
      :basketData="basketData"
      ref="customerBasket"
    />
    <div v-if="renderCustomerSeatingChart">
      <CustomerSeatingChart :chartData="chartData" ref="customerSeatingChart" />
    </div>
  </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref } from "vue";
// modules
import AuthModule from "@/store/modules/auth";
// components
import { isUserLoggedIn } from "@/utils/vue-helpers/user";

import CustomerSeatingChart from "@/views/Customer-Display/components/CustomerSeatingChart.vue";
import CustomerBasket from "@/views/Customer-Display/components/CustomerBasket.vue";
import { LogService } from "@/services/logging";
import { CustomerBasketData } from "@/utils/vue-helpers/customer-display";
import POSConfigModule from "@/store/modules/pos-config";

export default defineComponent({
  name: "CustomerDisplay",
  components: { CustomerSeatingChart, CustomerBasket },
  setup() {
    // creating a reference to the component CustomerSeatingChart
    // so that we can trigger CustomerSeatingChart.selectObjects() & CustomerSeatingChart.deselectObjects() functions
    const customerSeatingChart = ref<InstanceType<
      typeof CustomerSeatingChart
    > | null>(null);

    const customerBasket = ref<InstanceType<typeof CustomerBasket> | null>(
      null
    );
    const basketData = ref<CustomerBasketData | null>(null);
    let renderCustomerSeatingChart = ref<boolean>(false);
    let chartData = ref<any>(null);
    const isAuth = computed(() => {
      return AuthModule.getIsAuth;
    });

    function addSelectedSeatsOnChart(newlySelectedSeats: string[]) {
      customerSeatingChart.value?.selectObjects(newlySelectedSeats);
    }

    function removeSelectedSeatsOnChart(deselectedSeats: string[]) {
      customerSeatingChart.value?.deselectObjects(deselectedSeats);
    }

    const pictureUrl = computed(() => {
      return POSConfigModule.configPictureUrl;
    });

    return {
      isUserLoggedIn,
      isAuth,
      renderCustomerSeatingChart,
      chartData,
      customerSeatingChart,
      addSelectedSeatsOnChart,
      removeSelectedSeatsOnChart,
      customerBasket,
      basketData,
      pictureUrl,
    };
  },
  async mounted() {
    // This window's order store is empty (read-only window); the basket is
    // computed in the main window and received over the broadcast channel.
    const broadcastChannel = new BroadcastChannel("pos.smeetz");
    broadcastChannel.onmessage = async (event) => {
      const receivedData = JSON.parse(event.data);
      LogService.info("Attached data received in broadcast channel", {
        params: receivedData,
      });
      if (receivedData.isChartToUpdate) {
        this.renderCustomerSeatingChart =
          receivedData.renderCustomerSeatingChart;
        this.chartData = receivedData.chartData;
      }

      if (receivedData.isOrderUpdated && receivedData.customerBasketData) {
        this.basketData = receivedData.customerBasketData;
      }

      // 1- checking for newly selected seats
      if (
        receivedData.newlySelectedSeats &&
        receivedData.newlySelectedSeats.length
      ) {
        // So user has just selected new seats, lets select them in the already shown chart
        // do it by triggering a function inside CustomerSeatingChart and passing newlySelectedSeats variable
        this.addSelectedSeatsOnChart(receivedData.newlySelectedSeats);
      }

      // 2- checking for newly deselected seats
      if (receivedData.deselectedSeats && receivedData.deselectedSeats.length) {
        // So user has just deselected seats, lets deselect them in the already shown chart
        // do it by triggering a function inside CustomerSeatingChart and passing deselectedSeats variable
        this.removeSelectedSeatsOnChart(receivedData.deselectedSeats);
      }
    };

    // Ask the main window for the current basket snapshot
    broadcastChannel.postMessage(JSON.stringify({ requestBasket: true }));
  },
});
</script>
<style lang="scss" scoped>
.customer-display {
  background-color: #3d3f45;
  display: flex;
  width: 100%;
  background-size: auto 100%;
  background-position: right;
  background-repeat: no-repeat;
}
</style>
